import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, formatBRL, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FilePdf, FileCsv, ChartLineUp, MapTrifold, UsersThree } from "@phosphor-icons/react";

const INV_STATUS = {
  pending: "Pendente", paid: "Pago", overdue: "Vencido", cancelled: "Cancelado",
};
const INSP_STATUS = {
  scheduled: "Agendada", in_progress: "Em execução", completed: "Concluída", cancelled: "Cancelada",
};

function firstDayOfMonth() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function today() { return new Date().toISOString().slice(0, 10); }

function KpiCard({ label, value, tone = "primary" }) {
  const toneClass = tone === "green" ? "text-green-400" : tone === "accent" ? "text-accent" : tone === "destructive" ? "text-destructive" : "text-primary";
  return (
    <div className="tech-card p-5">
      <div className="label-tech text-muted-foreground">{label}</div>
      <div className={`text-2xl md:text-3xl font-black tracking-tight mt-1 ${toneClass}`} style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

function DateRangeBar({ start, end, setStart, setEnd, extra, onDownloadPdf, onDownloadCsv }) {
  return (
    <div className="tech-card p-4 mb-6 flex flex-wrap items-end gap-4">
      <div className="space-y-2">
        <Label className="label-tech">Início</Label>
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="report-start" />
      </div>
      <div className="space-y-2">
        <Label className="label-tech">Fim</Label>
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="report-end" />
      </div>
      {extra}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" className="gap-2" onClick={onDownloadCsv} data-testid="download-csv">
          <FileCsv size={16} weight="bold" /> CSV
        </Button>
        <Button className="gap-2" onClick={onDownloadPdf} data-testid="download-pdf">
          <FilePdf size={16} weight="bold" /> PDF
        </Button>
      </div>
    </div>
  );
}

// ============ FINANCIAL ============
function FinancialReport() {
  const [start, setStart] = useState(firstDayOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/financial", { params: { start, end } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const openReport = (fmt) => {
    const url = new URL(`${API}/reports/financial/${fmt}`);
    if (start) url.searchParams.set("start", start);
    if (end) url.searchParams.set("end", end);
    window.open(url.toString(), "_blank");
  };

  return (
    <div>
      <DateRangeBar
        start={start} end={end} setStart={setStart} setEnd={setEnd}
        onDownloadPdf={() => openReport("pdf")} onDownloadCsv={() => openReport("csv")}
      />
      {loading && <div className="label-tech text-muted-foreground">Carregando...</div>}
      {data && (
        <div className="space-y-6" data-testid="financial-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Total faturado" value={formatBRL(data.totals.invoiced)} />
            <KpiCard label="Pago" value={formatBRL(data.totals.paid)} tone="green" />
            <KpiCard label="Pendente" value={formatBRL(data.totals.pending)} tone="accent" />
            <KpiCard label="Vencido" value={formatBRL(data.totals.overdue)} tone="destructive" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard label="Nº de faturas" value={data.totals.count} />
            <KpiCard label="Ticket médio (pagas)" value={formatBRL(data.totals.avg_ticket)} tone="accent" />
            <KpiCard label="Taxa de recebimento" value={data.totals.invoiced ? `${Math.round((data.totals.paid / data.totals.invoiced) * 100)}%` : "—"} tone="green" />
          </div>

          <div className="tech-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Nº</th>
                  <th className="label-tech text-muted-foreground p-4">Cliente</th>
                  <th className="label-tech text-muted-foreground p-4">Descrição</th>
                  <th className="label-tech text-muted-foreground p-4">Emissão</th>
                  <th className="label-tech text-muted-foreground p-4 text-right">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.length === 0 ? (
                  <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">Sem faturas no período</td></tr>
                ) : data.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-border hover:bg-secondary/30">
                    <td className="p-4 mono text-sm">{i.number || "—"}</td>
                    <td className="p-4">{i.client_name}</td>
                    <td className="p-4 text-sm">{i.description}</td>
                    <td className="p-4 text-sm mono">{formatDate(i.issue_date)}</td>
                    <td className="p-4 text-right mono font-bold">{formatBRL(i.amount)}</td>
                    <td className="p-4"><Badge variant="outline">{INV_STATUS[i.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ OPERATIONAL ============
function OperationalReport() {
  const [start, setStart] = useState(firstDayOfMonth());
  const [end, setEnd] = useState(today());
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/reports/operational", { params });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, status]);

  const openReport = (fmt) => {
    const url = new URL(`${API}/reports/operational/${fmt}`);
    if (start) url.searchParams.set("start", start);
    if (end) url.searchParams.set("end", end);
    if (status !== "all") url.searchParams.set("status", status);
    window.open(url.toString(), "_blank");
  };

  return (
    <div>
      <DateRangeBar
        start={start} end={end} setStart={setStart} setEnd={setEnd}
        extra={
          <div className="space-y-2">
            <Label className="label-tech">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-44" data-testid="op-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(INSP_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
        onDownloadPdf={() => openReport("pdf")} onDownloadCsv={() => openReport("csv")}
      />
      {loading && <div className="label-tech text-muted-foreground">Carregando...</div>}
      {data && (
        <div className="space-y-6" data-testid="operational-report">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard label="Total de inspeções" value={data.totals.count} />
            <KpiCard label="Receita prevista" value={formatBRL(data.totals.revenue_forecast)} tone="accent" />
            <KpiCard label="Concluídas" value={data.by_status.completed?.count || 0} tone="green" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="tech-card p-6">
              <h3 className="text-lg font-extrabold tracking-tight mb-4" style={{ fontFamily: "Manrope" }}>Por status</h3>
              <div className="space-y-2">
                {Object.entries(data.by_status).map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center border-b border-border pb-2 last:border-0">
                    <span className="text-sm">{INSP_STATUS[k] || k}</span>
                    <span className="font-bold mono text-sm">{v.count} · {formatBRL(v.amount)}</span>
                  </div>
                ))}
                {Object.keys(data.by_status).length === 0 && <div className="text-muted-foreground text-sm">—</div>}
              </div>
            </div>
            <div className="tech-card p-6">
              <h3 className="text-lg font-extrabold tracking-tight mb-4" style={{ fontFamily: "Manrope" }}>Top cidades</h3>
              <div className="space-y-2">
                {data.by_city.map(([city, n]) => (
                  <div key={city} className="flex justify-between items-center border-b border-border pb-2 last:border-0">
                    <span className="text-sm">{city}</span>
                    <span className="font-bold mono text-sm">{n} inspeç{n === 1 ? "ão" : "ões"}</span>
                  </div>
                ))}
                {data.by_city.length === 0 && <div className="text-muted-foreground text-sm">—</div>}
              </div>
            </div>
          </div>

          <div className="tech-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Missão</th>
                  <th className="label-tech text-muted-foreground p-4">Cliente</th>
                  <th className="label-tech text-muted-foreground p-4">Data</th>
                  <th className="label-tech text-muted-foreground p-4">Local</th>
                  <th className="label-tech text-muted-foreground p-4">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.inspections.length === 0 ? (
                  <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">Sem inspeções no período</td></tr>
                ) : data.inspections.map((i) => (
                  <tr key={i.id} className="border-b border-border hover:bg-secondary/30">
                    <td className="p-4 font-bold">{i.title}</td>
                    <td className="p-4 text-sm">{i.client_name}</td>
                    <td className="p-4 text-sm mono">{formatDate(i.scheduled_start)}</td>
                    <td className="p-4 text-sm">{i.address_city ? `${i.address_city}/${i.address_state || ""}` : "—"}</td>
                    <td className="p-4 text-sm mono">{formatBRL(i.price || 0)}</td>
                    <td className="p-4"><Badge variant="outline">{INSP_STATUS[i.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CLIENT REPORT ============
function ClientReport() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
      if (data.length) setClientId(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        const { data } = await api.get(`/reports/client/${clientId}`, { params });
        setData(data);
      } finally { setLoading(false); }
    })();
  }, [clientId, start, end]);

  const openReport = (fmt) => {
    const url = new URL(`${API}/reports/client/${clientId}/${fmt}`);
    if (start) url.searchParams.set("start", start);
    if (end) url.searchParams.set("end", end);
    window.open(url.toString(), "_blank");
  };

  return (
    <div>
      <div className="tech-card p-4 mb-6 flex flex-wrap items-end gap-4">
        <div className="space-y-2 min-w-[240px]">
          <Label className="label-tech">Cliente</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="client-report-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="label-tech">Início (opcional)</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-2">
          <Label className="label-tech">Fim (opcional)</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => openReport("csv")} disabled={!clientId}>
            <FileCsv size={16} weight="bold" /> CSV
          </Button>
          <Button className="gap-2" onClick={() => openReport("pdf")} disabled={!clientId} data-testid="client-report-pdf">
            <FilePdf size={16} weight="bold" /> PDF
          </Button>
        </div>
      </div>

      {loading && <div className="label-tech text-muted-foreground">Carregando...</div>}
      {data && (
        <div className="space-y-6" data-testid="client-report">
          <div className="tech-card p-6">
            <div className="label-tech text-muted-foreground">Cliente</div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tighter mt-1" style={{ fontFamily: "Manrope" }}>{data.client.name}</h2>
            <div className="text-sm text-muted-foreground mt-1">
              {[data.client.document, data.client.email, data.client.phone].filter(Boolean).join(" · ")}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Recebido" value={formatBRL(data.totals.paid)} tone="green" />
            <KpiCard label="Pendente" value={formatBRL(data.totals.pending)} tone="accent" />
            <KpiCard label="Inspeções" value={`${data.totals.inspections_done}/${data.totals.inspections_total}`} />
            <KpiCard label="Docs (C/P)" value={`${data.totals.contracts} · ${data.totals.proposals}`} tone="accent" />
          </div>

          <div className="tech-card overflow-hidden">
            <div className="p-4 border-b border-border label-tech text-muted-foreground">Inspeções</div>
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Missão</th>
                  <th className="label-tech text-muted-foreground p-4">Data</th>
                  <th className="label-tech text-muted-foreground p-4">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.inspections.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">—</td></tr>
                ) : data.inspections.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0">
                    <td className="p-4 font-bold">{i.title}</td>
                    <td className="p-4 text-sm mono">{formatDate(i.scheduled_start)}</td>
                    <td className="p-4 text-sm mono">{formatBRL(i.price || 0)}</td>
                    <td className="p-4"><Badge variant="outline">{INSP_STATUS[i.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tech-card overflow-hidden">
            <div className="p-4 border-b border-border label-tech text-muted-foreground">Faturas</div>
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Nº</th>
                  <th className="label-tech text-muted-foreground p-4">Descrição</th>
                  <th className="label-tech text-muted-foreground p-4">Emissão</th>
                  <th className="label-tech text-muted-foreground p-4 text-right">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">—</td></tr>
                ) : data.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0">
                    <td className="p-4 mono text-sm">{i.number || "—"}</td>
                    <td className="p-4">{i.description}</td>
                    <td className="p-4 text-sm mono">{formatDate(i.issue_date)}</td>
                    <td className="p-4 text-right mono font-bold">{formatBRL(i.amount)}</td>
                    <td className="p-4"><Badge variant="outline">{INV_STATUS[i.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  return (
    <Layout title="Relatórios" subtitle="Análises financeiras, operacionais e por cliente — com exportação em PDF e CSV">
      <Tabs defaultValue="financial" className="w-full">
        <TabsList data-testid="reports-tabs" className="mb-6">
          <TabsTrigger value="financial" className="gap-2" data-testid="tab-financial">
            <ChartLineUp size={16} weight="bold" /> Financeiro
          </TabsTrigger>
          <TabsTrigger value="operational" className="gap-2" data-testid="tab-operational">
            <MapTrifold size={16} weight="bold" /> Operacional
          </TabsTrigger>
          <TabsTrigger value="client" className="gap-2" data-testid="tab-client">
            <UsersThree size={16} weight="bold" /> Por Cliente
          </TabsTrigger>
        </TabsList>
        <TabsContent value="financial"><FinancialReport /></TabsContent>
        <TabsContent value="operational"><OperationalReport /></TabsContent>
        <TabsContent value="client"><ClientReport /></TabsContent>
      </Tabs>
    </Layout>
  );
}
